import React from 'react';
import { useQuery } from 'react-query';
import { useRouting } from 'hooks/useRouting';
import { getHaito, getIncomeGain } from 'config/api';
import { formatNumber, formatDate } from 'utils/formatter';
import { DividendProps } from 'pages/DividendPrediction';
import { IncomeGainItemProps, IncomeGainProps } from 'pages/DividendDeposit';
import Loading from 'components/commons/loading/Loading';
import { isPremium } from 'components/features/utils/userStatus';
import { DividendPieChart } from './DividendChart';

const month = formatDate(new Date(), 'M');

const DividendLoading = () => {
  return (
    <div className="pt-8 pb-6 px-4">
      <div className="mb-1 text-lg font-bold">配当金管理</div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </div>
  );
};

const DividendNoDataChart = () => {
  return (
    <>
      <div className="">
        <img
          src="/static/images/home/dividend_no_data.svg"
          alt="パイチャートのイメージ"
          className="mx-auto"
          height="255"
          width="255"
        />
      </div>
      <div className="mt-6 text-sm text-bluegray-500 text-center">
        配当がある国内株式を保有している
        <br />
        場合に配当情報が表示されます
      </div>
    </>
  );
};

const GoToDividendPrediction = () => {
  const routing = useRouting();
  const goDividendprediction = (): void => {
    routing.pushRouting({ path: `/dividendprediction` }, true, window.scrollY);
  };
  return (
    <div className="flex justify-end mt-4 mx-4">
      <div
        className="flex flex-row text-blue-700 cursor-pointer"
        onClick={(): void => goDividendprediction()}>
        <div className="icon-arrow_right text-lg" />
        <div className="text-xs">配当金管理へ</div>
      </div>
    </div>
  );
};

const DividendNoData = () => {
  return (
    <div className="pt-8">
      <div className="px-4 mb-1 text-lg font-bold">配当金管理</div>
      <div className="text-base text-center font-bold">
        {month}月<span className="pl-1">配当予想</span>
      </div>
      <div className="mt-2 mb-4 text-xs text-center">
        <span className="pr-1 text-2xl font-bold">---</span>
        円(税込)
      </div>
      <DividendNoDataChart />
      <div className="mt-6 mx-4 font-bold">配当入金額</div>
      <div className="flex flex-row m-4">
        <div className="flex flex-col pl-4 text-xs">
          <div className="font-bold">今年</div>
          <div>1月~{month}月</div>
        </div>
        <div className="ml-auto mr-4 text-xs">
          <span className="pr-1 text-base font-bold">---</span>円
        </div>
      </div>
      <div className="mx-4 border-t border-bluegray-300" />
      <GoToDividendPrediction />
    </div>
  );
};

export const DividendItemInternal = ({
  dividendData,
  totalIncomeGain,
}: {
  dividendData: DividendProps[];
  totalIncomeGain: number;
}) => {
  const totalDividend = dividendData
    .map(data => data.haito)
    .reduce((acc, current) => acc + current, 0);

  const isNoDividendData = dividendData.length === 0;

  const routing = useRouting();
  const goDividendprediction = (): void => {
    routing.pushRouting({ path: `/dividendprediction` }, true, window.scrollY);
  };

  const goDividenddeposit = (): void => {
    if (isNoDividendData) return;
    routing.pushRouting({ path: `/dividenddeposit` }, true, window.scrollY);
  };

  return (
    <div className="pt-8">
      <div className="px-4 mb-1 text-lg font-bold">配当金管理</div>
      <div className="text-base text-center font-bold">
        {month}月<span className="pl-1">配当予想</span>
      </div>
      <div className="mt-2 mb-4 text-xs text-center">
        <span className="pr-1 text-2xl font-bold">
          {isNoDividendData ? '---' : formatNumber(totalDividend, {})}
        </span>
        円(税込)
      </div>
      {isNoDividendData ? (
        <DividendNoDataChart />
      ) : (
        <>
          <DividendPieChart {...{ dividendList: dividendData }} />
          <div className="mx-4 text-sm text-bluegray-700 text-right">
            ※国内株式のみ表示
          </div>
          <div
            className="h-10 flex items-center justify-center mt-4 mx-4 text-base text-bluegray-500 border rounded-lg cursor-pointer"
            onClick={(): void => goDividendprediction()}>
            詳細を見る
          </div>
        </>
      )}

      <div className="mt-6 mx-4 font-bold">配当入金額</div>
      <div
        className={`flex flex-row m-4 ${
          isNoDividendData ? '' : 'cursor-pointer'
        }`}
        onClick={(): void => goDividenddeposit()}>
        <div className="flex flex-col pl-4 text-xs">
          <div className="font-bold">今年</div>
          <div>1月~{month}月</div>
        </div>
        <div className="ml-auto mr-4 text-xs">
          <span className="pr-1 text-base font-bold">
            {formatNumber(totalIncomeGain, {})}
          </span>
          円
        </div>
        {!isNoDividendData ? (
          <div className="flex items-center mx-4 w-2">
            <img
              className="w-full"
              src="/static/images/icon/icon-arrow-right.svg"
              alt=""
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      <div className="mx-4 border-t border-bluegray-300" />
      {isNoDividendData ? <GoToDividendPrediction /> : <></>}
    </div>
  );
};

const GetDividendData = () => {
  const { data: dividendData } = useQuery('getHomeHaito', () => getHaitoAPI(), {
    refetchOnWindowFocus: false,
  });

  const year = formatDate(new Date(), 'yyyy');
  const from = `${year}01`;
  const to = formatDate(new Date(), 'yyyyMM');

  const { data: incomeGainData } = useQuery(
    'getHomeIncomeGain',
    () => getIncomeGainAPI(from, to),
    {
      refetchOnWindowFocus: false,
    }
  );

  if (dividendData === undefined || incomeGainData === undefined)
    return <DividendLoading />;
  if (
    dividendData.length === 0 &&
    (incomeGainData.length === 0 || incomeGainData.items.length === 0)
  )
    return <DividendNoData />;

  const dividendList = dividendData as DividendProps[];

  const targetDividendData = dividendList.filter(data => data.haitoYm === to);

  if (
    targetDividendData.length === 0 &&
    (incomeGainData.length === 0 || incomeGainData.items.length === 0)
  )
    return <DividendNoData />;

  const incomeGainDataList = incomeGainData as IncomeGainProps;

  const totalIncomeGain = incomeGainDataList.items
    .map(data => (data.amount === null ? 0 : data.amount))
    .reduce((acc, current) => acc + current, 0);

  return (
    <DividendItemInternal
      {...{ dividendData: targetDividendData, totalIncomeGain }}
    />
  );
};

export const DividendItem = () => {
  if (isPremium()) return <GetDividendData />;
  return <DividendNoData />;
};

const getHaitoAPI = async () => {
  const ret_ = await getHaito();
  const ret = ret_.Items.map(item => {
    return {
      symbol: item.Symbol,
      symbolName: item.SymbolName,
      haito: item.Haito,
      taxExcludedHaito: item.TaxExcludedHaito,
      qty: item.Qty,
      accountType: item.AccountType,
      // memo: apiレスポンスを yyyy.mm -> yyyymm に変換する
      haitoYm: item.HaitoYm.replace('.', ''),
      unreleased: item.Unreleased,
      hasNew: item.HasNew,
    };
  }) as DividendProps[];

  return ret;
};

const getIncomeGainAPI = async (from: string, to: string) => {
  const ret_ = await getIncomeGain(from, to);

  const ret = {
    from: ret_.From,
    to: ret_.To,
    items: ret_.Items.map(item => {
      return {
        account: item.Account,
        brokerID: item.BrokerID,
        brokerName: item.BrokerName,
        symbol: item.Symbol,
        symbolName: item.SymbolName,
        productType: item.ProductType,
        amount: item.Amount,
        date: item.Date,
        isin: item.ISIN,
        hasNew: item.HasNew,
      };
    }) as IncomeGainItemProps[],
  } as IncomeGainProps;

  return ret;
};
